import React from 'react'
import { Link } from 'react-router-dom'
import { Phone, Mail, MapPin, ShieldCheck, Award } from 'lucide-react'

export default function Footer() {
  return (
    <footer className="bg-[#20252C] text-gray-300 border-t border-gray-800">
      {/* Main Footer Links & Info */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Column 1: Brand & CVR */}
          <div className="flex flex-col gap-4">
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src="https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/3fcf9262-0e4a-4a97-9828-7c0f98c2c183.png"
                alt="Malerfirma Villy Poulsen Logo"
                className="h-20 w-auto object-contain"
              />
            </Link>
            <p className="text-sm text-gray-400 leading-relaxed">
              Kvalitetsbevidst malerfirma beliggende i Spentrup nær Randers og Hobro. Vi dækker hele Østjylland med professionelt malerarbejde.
            </p>
            <div className="text-xs text-gray-500 mt-2">
              <p>CVR nr.: 10082021</p>
              <p>© {new Date().getFullYear()} Malerfirma Villy Poulsen ApS. Alle rettigheder forbeholdes.</p>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-4 text-xs text-gray-400">
              <a 
                href="https://www.iubenda.com/privacy-policy/52527719/cookie-policy" 
                className="iubenda-nostyle iubenda-noiframe iubenda-embed hover:text-white transition-colors" 
                title="Cookiepolitik"
              >
                Cookiepolitik
              </a>
              <span className="text-gray-700">|</span>
              <a 
                href="https://www.iubenda.com/privacy-policy/52527719" 
                className="iubenda-nostyle iubenda-noiframe iubenda-embed hover:text-white transition-colors" 
                title="Privatlivspolitik"
              >
                Privatlivspolitik
              </a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-6">
              Hurtige Links
            </h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/" className="hover:text-[#01529E] transition-colors">Forside</Link>
              </li>
              <li>
                <Link to="/vi-udfoerer" className="hover:text-[#01529E] transition-colors">Vi udfører</Link>
              </li>
              <li>
                <Link to="/indvendig-maling" className="hover:text-[#01529E] transition-colors">Indvendig maling</Link>
              </li>
              <li>
                <Link to="/udvendig-maling" className="hover:text-[#01529E] transition-colors">Udvendig maling</Link>
              </li>
              <li>
                <Link to="/sproejtemaling" className="hover:text-[#01529E] transition-colors">Sprøjtemaling</Link>
              </li>
              <li>
                <Link to="/renovering-og-vedligeholdelse" className="hover:text-[#01529E] transition-colors">Renovering & Vedligeholdelse</Link>
              </li>
              <li>
                <Link to="/galleri" className="hover:text-[#01529E] transition-colors">Billedgalleri</Link>
              </li>
              <li>
                <Link to="/om-os" className="hover:text-[#01529E] transition-colors">Om os</Link>
              </li>
              <li>
                <Link to="/kontakt" className="hover:text-[#01529E] transition-colors">Kontakt</Link>
              </li>
            </ul>
          </div>

          {/* Column 3: Contact Info */}
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-6">
              Kontaktoplysninger
            </h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#ED1C26] shrink-0 mt-0.5" />
                <span className="leading-relaxed">
                  Ydunsvej 5A,<br />
                  DK-8981 Spentrup
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-[#ED1C26] shrink-0" />
                <a href="tel:40637670" className="hover:text-[#01529E] transition-colors whitespace-nowrap">
                  Telefon: 40 63 76 70
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-[#ED1C26] shrink-0" />
                <a href="mailto:mail@malerfirma-villy-poulsen.dk" className="hover:text-[#01529E] transition-colors whitespace-nowrap">
                  mail@malerfirma-villy-poulsen.dk
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4: Trust & Badges */}
          <div className="flex flex-col gap-6">
            <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-2">
              Garanti & Tillid
            </h4>
            <div className="flex flex-col gap-4">
              {/* Danske Malermestre */}
              <a
                href="https://malermestre.dk/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-gray-800/50 p-3 rounded-lg border border-gray-800 hover:border-[#01529E]/30 transition-all group"
              >
                <img
                  src="https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/3345aa26-27bc-4396-be9b-17210b963880.png"
                  alt="Medlem af Danske Malermestre"
                  className="h-10 object-contain group-hover:scale-105 transition-transform"
                />
                <div className="text-xs">
                  <p className="font-bold text-white">Danske Malermestre</p>
                  <p className="text-gray-400">Din garanti for kvalitet</p>
                </div>
              </a>

              {/* AAA Rating */}
              <div
                className="flex items-center gap-3 bg-gray-800/50 p-3 rounded-lg border border-gray-800 hover:border-[#01529E]/30 transition-all group"
              >
                <img
                  src="https://assets.auxodev.dk/assets/bbcf0f14-2bcd-4cd4-98b4-2d62ce00d60f/c676648e-1c3d-472f-9b84-3e2df456474c.png"
                  alt="AAA Højeste Kreditværdighed"
                  className="h-10 object-contain group-hover:scale-105 transition-transform"
                />
                <div className="text-xs">
                  <p className="font-bold text-white">AAA Rating</p>
                  <p className="text-gray-400">Højeste kreditværdighed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
